/**
 * ss-ui.js, website v5 (brand v4.0). One file, no framework, no build step.
 * Source of truth: 01-brand/v4/design-system-v6-notes.md §4 (motion), §7 (overlays), §9 (mobile
 * contract), §5 (diffusion field). Styles: ss-components.css (Part 1).
 *
 * Provides
 *   Overlays   <dialog class="ss-drawer|ss-sheet|ss-viewer"> opened by [data-ss-open="<id>"],
 *              closed by [data-ss-close], Escape, or the backdrop. Native showModal() gives the
 *              W3C dialog contract (focus trap, inert page, Escape, top layer); this file adds the
 *              transition classes, scroll lock, focus return, "never stacked" (opening one closes
 *              the open one first), and body[data-ss-drawer-open] for the dock and the chat bubble.
 *              Impact's own dialogs (cart, search, menu) are left to theme.js; opening an ss overlay
 *              while one of them is open closes it first through its hide().
 *   Viewer     [data-ss-view] (or [data-ss-view-src]) opens the shared image viewer with the
 *              element's src/alt/caption. One <dialog class="ss-viewer"> is created on demand.
 *   Tabs       .ss-tabs with [role=tablist] > .ss-tab[aria-controls] and [role=tabpanel] panels.
 *   Toast      SSUI.toast({ text, action, onAction, kind, duration }). One toast at a time.
 *   Buy dock   .ss-dock[data-ss-dock] shows after [data-ss-dock-anchor] has scrolled off screen,
 *              hides with any overlay or the on-screen keyboard; its button clicks the anchor.
 *   Chat       [data-ss-chat-toggle] in the header opens the storefront chat app embed through its
 *              hidden bubble and anchors the window under the button (styles: ss-apps.css).
 *   Diffusion  [data-ss-diffusion] tap turns the closest .ss-diffusion on once (2.8 s, then still).
 *
 * Events (bubbling, on the dialog): ss:open, ss:close. On document: ss:toast.
 */
(function () {
  'use strict';
  if (window.SSUI) return;

  var doc = document;
  var root = doc.documentElement;
  var OVERLAY_SEL = 'dialog.ss-drawer, dialog.ss-sheet, dialog.ss-viewer';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function motionMs(name) {
    var v = getComputedStyle(root).getPropertyValue(name).trim();
    var n = parseFloat(v);
    if (!isFinite(n)) return 0;
    if (/^[\d.]+s$/.test(v)) n *= 1000;
    return (reduced && reduced.matches) ? 0 : n;
  }

  function kindOf(dialog) {
    if (dialog.classList.contains('ss-sheet')) return 'sheet';
    if (dialog.classList.contains('ss-viewer')) return 'viewer';
    return 'drawer';
  }

  /* ---------- scroll lock (own counter; Impact uses html.lock for its dialogs) ---------- */
  var lockCount = 0;
  function lock() { if (lockCount++ === 0) root.classList.add('ss-lock'); }
  function unlock() { if (lockCount > 0 && --lockCount === 0) root.classList.remove('ss-lock'); }

  /* ---------- overlays ---------- */
  var openDialog = null;
  var lastOpener = null;

  function closeImpactDialogs() {
    doc.querySelectorAll('.drawer[open], dialog-element[open], x-drawer[open], newsletter-popup[open], quick-buy-drawer[open], facet-drawer[open]').forEach(function (el) {
      if (typeof el.hide === 'function') el.hide();
      else el.removeAttribute('open');
    });
  }

  function open(dialog, opener) {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    if (openDialog === dialog) return;
    if (openDialog) close(openDialog, true);      // never stacked
    closeImpactDialogs();
    lastOpener = opener || doc.activeElement;
    openDialog = dialog;
    dialog.showModal();
    lock();
    doc.body.setAttribute('data-ss-drawer-open', kindOf(dialog));
    // next frame so the transition runs from the closed state
    requestAnimationFrame(function () { requestAnimationFrame(function () { dialog.classList.add('is-open'); }); });
    var first = dialog.querySelector('[autofocus]') || dialog.querySelector('.ss-drawer__close, .ss-sheet__close, .ss-viewer__close') || dialog.querySelector(FOCUSABLE);
    if (first) first.focus({ preventScroll: true });
    dialog.dispatchEvent(new CustomEvent('ss:open', { bubbles: true }));
  }

  function close(dialog, immediate) {
    dialog = dialog || openDialog;
    if (!dialog || !dialog.open) return;
    // Re-entry guard: Escape, the backdrop and the close button can all land within one frame,
    // and without this the dialog would schedule two teardowns and fire ss:close twice.
    if (dialog.dataset.ssClosing) return;
    dialog.dataset.ssClosing = '1';
    dialog.classList.remove('is-open');
    var finish = function () {
      delete dialog.dataset.ssClosing;
      if (dialog.open) dialog.close();
      if (openDialog === dialog) {
        openDialog = null;
        doc.body.removeAttribute('data-ss-drawer-open');
        unlock();
        var back = lastOpener;
        lastOpener = null;
        if (back && typeof back.focus === 'function' && doc.contains(back)) back.focus({ preventScroll: true });
      }
      dialog.dispatchEvent(new CustomEvent('ss:close', { bubbles: true }));
    };
    var ms = immediate ? 0 : motionMs('--ss-motion-panel');
    if (ms > 0) setTimeout(finish, ms + 20); else finish();
  }

  function onOpenClick(e) {
    var trigger = e.target.closest('[data-ss-open]');
    if (!trigger) return;
    var id = trigger.getAttribute('data-ss-open');
    var dialog = id && doc.getElementById(id);
    if (!dialog || !dialog.matches(OVERLAY_SEL)) return;
    e.preventDefault();
    open(dialog, trigger);
  }

  function onCloseClick(e) {
    var dialog = e.target.closest(OVERLAY_SEL);
    if (!dialog) return;
    if (e.target.closest('[data-ss-close]')) { e.preventDefault(); close(dialog); return; }
    // backdrop: a click on the dialog element itself or on the backdrop layer, not inside the panel
    if (e.target === dialog || e.target.classList.contains('ss-drawer__backdrop') || e.target.classList.contains('ss-sheet__backdrop') || e.target.classList.contains('ss-viewer__backdrop') || e.target.classList.contains('ss-viewer__panel')) {
      close(dialog);
    }
  }

  // Escape: the platform fires "cancel" then closes; intercept so the transition and cleanup run.
  function onCancel(e) {
    var dialog = e.target.closest && e.target.closest(OVERLAY_SEL);
    if (!dialog) return;
    e.preventDefault();
    close(dialog);
  }

  // Escape fallback. On the live storefront the platform close request can be consumed before the
  // dialog sees it, so "cancel" never fires and a modal stays open on Escape even though the close
  // button and the backdrop both work. Measured on the DRAFT 2026-09-11: dialog :modal true, not
  // inert, parented to body, keydown not defaultPrevented, and neither cancel nor close dispatched.
  // The v7 overlay contract says Escape closes, so close on the key directly. close() is guarded,
  // so where cancel does fire normally this costs nothing.
  function onEscape(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    if (e.defaultPrevented || !openDialog) return;
    e.preventDefault();
    close(openDialog);
  }

  // Safety net: if something else closes the dialog (dialog.close() from other code), clean up.
  function onNativeClose(e) {
    var dialog = e.target;
    if (!dialog.matches || !dialog.matches(OVERLAY_SEL)) return;
    if (openDialog === dialog) {
      dialog.classList.remove('is-open');
      openDialog = null;
      doc.body.removeAttribute('data-ss-drawer-open');
      unlock();
    }
  }

  /* ---------- image viewer (shared element, created on first use) ---------- */
  var viewer = null;
  function ensureViewer() {
    if (viewer) return viewer;
    viewer = doc.createElement('dialog');
    viewer.className = 'ss-viewer';
    viewer.id = 'ss-viewer';
    viewer.setAttribute('aria-label', 'Image');
    viewer.innerHTML =
      '<div class="ss-viewer__backdrop"></div>' +
      '<div class="ss-viewer__panel">' +
        '<img class="ss-viewer__img" alt="" decoding="async">' +
        '<p class="ss-viewer__caption" hidden></p>' +
        '<button type="button" class="ss-viewer__close" data-ss-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
        '</button>' +
      '</div>';
    doc.body.appendChild(viewer);
    return viewer;
  }

  function view(src, alt, caption, opener) {
    if (!src) return;
    var v = ensureViewer();
    var img = v.querySelector('.ss-viewer__img');
    var cap = v.querySelector('.ss-viewer__caption');
    img.src = src;
    img.alt = alt || '';
    if (caption) { cap.textContent = caption; cap.hidden = false; } else { cap.textContent = ''; cap.hidden = true; }
    open(v, opener);
  }

  function onViewClick(e) {
    var t = e.target.closest('[data-ss-view], [data-ss-view-src]');
    if (!t) return;
    var src = t.getAttribute('data-ss-view-src') || t.getAttribute('href') || (t.tagName === 'IMG' ? (t.currentSrc || t.src) : null);
    if (!src) { var img = t.querySelector('img'); if (img) src = img.currentSrc || img.src; }
    if (!src) return;
    e.preventDefault();
    var alt = t.getAttribute('data-ss-view-alt') || (t.tagName === 'IMG' ? t.alt : (t.querySelector('img') ? t.querySelector('img').alt : ''));
    view(src, alt, t.getAttribute('data-ss-view-caption') || '', t);
  }

  /* ---------- tabs ---------- */
  function initTabs(scope) {
    (scope || doc).querySelectorAll('.ss-tabs').forEach(function (tabs) {
      if (tabs.dataset.ssTabsReady) return;
      tabs.dataset.ssTabsReady = '1';
      var list = tabs.querySelector('[role="tablist"]');
      if (!list) return;
      var tabEls = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
      function panelOf(tab) { var id = tab.getAttribute('aria-controls'); return id ? doc.getElementById(id) : null; }
      function select(tab, focus) {
        tabEls.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var p = panelOf(t);
          if (p) p.hidden = !on;
        });
        if (focus) tab.focus();
        tabs.dispatchEvent(new CustomEvent('ss:tab', { bubbles: true, detail: { tab: tab } }));
      }
      tabEls.forEach(function (t, i) {
        t.addEventListener('click', function () { select(t, false); });
        t.addEventListener('keydown', function (e) {
          var j = i;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = (i + 1) % tabEls.length;
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = (i - 1 + tabEls.length) % tabEls.length;
          else if (e.key === 'Home') j = 0;
          else if (e.key === 'End') j = tabEls.length - 1;
          else return;
          e.preventDefault();
          select(tabEls[j], true);
        });
      });
      var initial = tabEls.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabEls[0];
      if (initial) select(initial, false);
    });
  }

  /* ---------- toast ---------- */
  var toastEl = null, toastTimer = null;
  function toast(opts) {
    if (typeof opts === 'string') opts = { text: opts };
    opts = opts || {};
    if (!toastEl) {
      toastEl = doc.createElement('div');
      toastEl.className = 'ss-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      doc.body.appendChild(toastEl);
    }
    clearTimeout(toastTimer);
    toastEl.className = 'ss-toast' + (opts.kind ? ' ss-toast--' + opts.kind : '');
    toastEl.textContent = '';
    var span = doc.createElement('span');
    span.textContent = opts.text || '';
    toastEl.appendChild(span);
    if (opts.action) {
      var btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'ss-toast__action';
      btn.textContent = opts.action;
      btn.addEventListener('click', function () { hideToast(); if (typeof opts.onAction === 'function') opts.onAction(); });
      toastEl.appendChild(btn);
    }
    // reflow so the transition restarts when a toast replaces another
    void toastEl.offsetWidth;
    toastEl.classList.add('is-open');
    doc.dispatchEvent(new CustomEvent('ss:toast', { detail: opts }));
    toastTimer = setTimeout(hideToast, opts.duration || (opts.action ? 6000 : 3200));
    return toastEl;
  }
  function hideToast() { clearTimeout(toastTimer); if (toastEl) toastEl.classList.remove('is-open'); }

  /* ---------- buy dock ---------- */
  function initDock() {
    var dock = doc.querySelector('.ss-dock[data-ss-dock]');
    if (!dock || dock.dataset.ssDockReady) return;
    var anchor = doc.querySelector('[data-ss-dock-anchor]');
    if (!anchor) return;
    dock.dataset.ssDockReady = '1';
    var passed = false, keyboard = false;
    function overlayOpen() {
      return doc.body.hasAttribute('data-ss-drawer-open') || !!doc.querySelector('.drawer[open], dialog[open], dialog-element[open], newsletter-popup[open], .ss-popup.is-open, body.shop-ai-chat-open');
    }
    function update() {
      var overlay = overlayOpen();
      var show = passed && !keyboard && !overlay;
      // state for the verify scripts and the console; no behaviour depends on it
      dock.setAttribute('data-ss-dock-state', (passed ? 'passed' : 'before') + (keyboard ? ' keyboard' : '') + (overlay ? ' overlay' : ''));
      dock.classList.toggle('is-visible', show);
      dock.setAttribute('aria-hidden', show ? 'false' : 'true');
      if (show) dock.removeAttribute('inert'); else dock.setAttribute('inert', '');
      doc.body.classList.toggle('ss-dock-open', show);
      if (show) root.style.setProperty('--ss-dock-h', dock.offsetHeight + 'px');
    }
    // Measured on scroll, not with an IntersectionObserver: an observer only reports a change of
    // intersection state, so a jump from "below the screen" to "above the screen" (anchor links,
    // scrollTo, a fast flick) never fires it. One getBoundingClientRect per frame while scrolling.
    var ticking = false;
    function measure() {
      ticking = false;
      // only after the button has left through the top (scrolled past), not before it is reached
      passed = anchor.getBoundingClientRect().bottom < 0;
      update();
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(measure); } }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    // keyboard: an editable element focused on a touch screen, or the visual viewport shrinking
    doc.addEventListener('focusin', function (e) { if (e.target.matches('input:not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]), textarea, select, [contenteditable]')) { keyboard = true; update(); } });
    doc.addEventListener('focusout', function () { setTimeout(function () { keyboard = !!(doc.activeElement && doc.activeElement.matches('input, textarea, select, [contenteditable]')); update(); }, 50); });
    if (window.visualViewport) {
      var base = window.visualViewport.height;
      window.visualViewport.addEventListener('resize', function () {
        var h = window.visualViewport.height;
        if (h > base) base = h;
        keyboard = keyboard || (base - h > 150);
        if (base - h <= 150 && !(doc.activeElement && doc.activeElement.matches('input, textarea, select'))) keyboard = false;
        update();
      });
    }
    doc.addEventListener('ss:open', update);
    doc.addEventListener('ss:close', function () { setTimeout(update, 30); });
    doc.addEventListener('dialog:after-show', update);
    doc.addEventListener('dialog:after-hide', function () { setTimeout(update, 30); });
    // The dock's button acts on the anchor (same form, same variant, same plan): no second state.
    var btn = dock.querySelector('[data-ss-dock-button]');
    if (btn) btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (typeof anchor.click === 'function') anchor.click();
    });
    // Mirror the price and the label from the anchor form so dock content equals the page state.
    function mirror() {
      var price = dock.querySelector('[data-ss-dock-price]');
      var src = doc.querySelector('[data-ss-dock-price-source]');
      if (price && src) price.textContent = src.textContent.trim();
      var label = dock.querySelector('[data-ss-dock-label]');
      var srcLabel = anchor.querySelector('[data-ss-dock-label-source], .button__content');
      var text = srcLabel ? srcLabel.textContent : Array.prototype.filter.call(anchor.childNodes, function (n) { return n.nodeType === 3; }).map(function (n) { return n.textContent; }).join('');
      if (label && text && text.trim()) label.textContent = text.trim();
    }
    mirror();
    doc.addEventListener('variant:change', function () { setTimeout(mirror, 60); });
    doc.addEventListener('sealsubs:price_update', function () { setTimeout(mirror, 60); });
    doc.addEventListener('ss:plan-change', function () { setTimeout(mirror, 0); });
    measure();
  }

  /* ---------- diffusion field: once per element, on tap, then still ---------- */
  function onDiffusion(e) {
    var t = e.target.closest('[data-ss-diffusion]');
    if (!t) return;
    var field = t.closest('.ss-diffusion') || t;
    if (field.dataset.ssDiffused) return;
    field.dataset.ssDiffused = '1';
    var r = field.getBoundingClientRect();
    if (field.dataset.ssDiffusionOrigin !== 'product' && e.clientX && r.width) {
      field.style.setProperty('--ss-diffusion-x', Math.round(((e.clientX - r.left) / r.width) * 100) + '%');
      field.style.setProperty('--ss-diffusion-y', Math.round(((e.clientY - r.top) / r.height) * 100) + '%');
    }
    field.classList.add('is-on');
  }

  /* Impact's hover summaries follow their data-url on a keyboard-generated click.
     Open the existing disclosure explicitly so keyboard users can reach its links;
     native Impact transitions and focus-out still own the menu. */
  function onHeaderKey(e) {
    if (e.key === 'Escape') {
      var openMenu = e.target.closest('.header details[open][is="mega-menu-disclosure"], .header details[open][is="dropdown-disclosure"]');
      if (!openMenu) return;
      openMenu.open = false;
      openMenu.firstElementChild.focus();
      return;
    }
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'ArrowDown') return;
    var summary = e.target.closest('.header summary');
    var menu = summary && summary.parentElement;
    if (!menu || !menu.matches('details[is="mega-menu-disclosure"][trigger="hover"], details[is="dropdown-disclosure"][trigger="hover"]')) return;
    e.preventDefault();
    if (e.repeat) return;
    menu.open = e.key === 'ArrowDown' || !menu.open;
    if (e.key === 'ArrowDown') {
      var firstLink = menu.lastElementChild.querySelector('a[href]');
      if (firstLink) firstLink.focus();
    }
  }

  /* ---------- wiring ---------- */
  doc.addEventListener('click', onOpenClick);
  doc.addEventListener('click', onCloseClick);
  doc.addEventListener('click', onViewClick);
  doc.addEventListener('click', onDiffusion);
  doc.addEventListener('cancel', onCancel, true);
  doc.addEventListener('close', onNativeClose, true);
  doc.addEventListener('keydown', onEscape, true);
  doc.addEventListener('keydown', onHeaderKey, true);

  /* ---------- header chat launcher ---------- */
  // The storefront chat app embed (chat-interface.liquid) owns the chat window and its state; its
  // only entry point is a click on .shop-ai-chat-bubble, which ss-apps.css hides while the header
  // launcher exists. Forward the click, anchor the window under the launcher, mirror the state.
  function initChatLauncher() {
    var btn = doc.querySelector('[data-ss-chat-toggle]');
    var bubble = doc.querySelector('.shop-ai-chat-container .shop-ai-chat-bubble');
    var win = doc.querySelector('.shop-ai-chat-container .shop-ai-chat-window');
    if (!btn || !bubble || !win || btn.dataset.ssChatReady) return;
    btn.dataset.ssChatReady = '1';
    var ticking = false;
    function place() {
      ticking = false;
      if (!btn.getBoundingClientRect().width) return;
      // under the bottom-right corner of the header pill that holds the icons
      var r = (btn.closest('.header__secondary-nav') || btn).getBoundingClientRect();
      root.style.setProperty('--ss-chat-top', Math.round(r.bottom + 8) + 'px');
      root.style.setProperty('--ss-chat-right', Math.max(12, Math.round(root.clientWidth - r.right)) + 'px');
    }
    function isOpen() { return win.classList.contains('active'); }
    function follow() { if (isOpen() && !ticking) { ticking = true; requestAnimationFrame(place); } }
    place();
    btn.addEventListener('click', function () { place(); bubble.click(); });
    new MutationObserver(function () {
      var open = isOpen();
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open && win.contains(doc.activeElement) && btn.isConnected) btn.focus({ preventScroll: true });
    }).observe(win, { attributes: true, attributeFilter: ['class'] });
    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !isOpen()) return;
      if (!win.contains(doc.activeElement) && doc.activeElement !== btn) return;
      var close = win.querySelector('.shop-ai-chat-close');
      if (close) close.click();
    });
    window.addEventListener('scroll', follow, { passive: true });
    window.addEventListener('resize', follow);
    doc.addEventListener('transitionend', follow); // the header pill changes size after scrolling
  }

  function boot() { initTabs(); initDock(); initChatLauncher(); }
  // Native details[name] supplies grouping in modern browsers; this also covers older engines.
  doc.addEventListener('toggle', function(e) {
    var detail = e.target;
    if (!detail.matches('.ss-science-story__detail[open]')) return;
    detail.closest('.ss-science-story').querySelectorAll('.ss-science-story__detail[open]').forEach(function(other) {
      if (other !== detail) other.open = false;
    });
  }, true);

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot); else boot();
  doc.addEventListener('shopify:section:load', function (e) { initTabs(e.target); initDock(); initChatLauncher(); });

  window.SSUI = { open: open, close: close, view: view, toast: toast, hideToast: hideToast, initTabs: initTabs, initDock: initDock };
})();
